import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"
import Image from "next/image"
import Link from "next/link"

import * as Sentry from "@sentry/nextjs"

import {
    endOfWeek, startOfDay, startOfWeek,
    useMachine, useProfile, useRestoredUniverse, useUpdateableState,
    fbTrackEvent
} from "@lib"

import { CnxBooking, CnxMeeting } from "@firmament-packages/connectx"
import { CnxMeetingListStateMachine } from "@machines/CnxMeetingListStateMachine"
import { AffAttendeeProfile,  } from "@firmament-packages/aff"

import { SegmentedControl, Segment } from "@components/SegmentedControl"
import WeekView from "@components/WeekView"
import AFFSidebarLayout from "@components/domain/AFFSidebarLayout"
import { CnxMeetingCell } from "@components/domain/CnxMeeting"
import LoadingIndicator from "@components/LoadingIndicator"
import ErrorHUD from "@components/ErrorHUD"
import Modal from "@components/Modal"

const MARKETING_WRITE_UP = `All Alive, Fit & Free classes & events are
offered live online. All you need to participate is an internet connection and
smartphone, tablet, or laptop. All of our classes are adaptive, which means we
offer multiple variations of movements to cater to every ability level,
both standing or seated.
`

const MarketingHeader: React.FunctionComponent<{}> = ({}) => (
    <div className="relative">
        <div className="absolute w-full h-full">
            <Image layout="fill" objectFit="cover" src="/images/bg_marketing_banner.jpg" alt={MARKETING_WRITE_UP} />
        </div>

        <div className="flex py-[32px]">
            <div className="relative w-[90%] sm:w-[64%] max-w-[640px] mx-auto">
                <div className="border-[4px] border-black-900">
                    <div className="bg-white-900 m-[8px] p-[20px] sm:p-[40px]">
                        <h1 className="text-title1 text-black-900 text-center">Live Classes</h1>
                        <h5 className="mt-[12px] text-subhead text-black-900 text-center">
                            {MARKETING_WRITE_UP}
                        </h5>
                        <a href="https://www.loom.com/share/360f5226e6dd4debaa8e0f48e7c1eba7" target="_blank" rel="noopener noreferrer">
                            <div className="mt-[20px] text-callout text-center uppercase underline hover:text-accent-900">
                                Watch Tutorial
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
)

const MeetingList: React.FunctionComponent<{
    meetings: CnxMeeting[],
    segment?: "ALL" | "BOOKED",
    startDate: Date,
    onShowMeeting?: (meeting: CnxMeeting) => void,
    onBookButtonClick?: (meeting: CnxMeeting) => void,
    onCancelButtonClick?: (meeting: CnxMeeting, booking: CnxBooking) => void,
    onEnterButtonClick?: (meeting: CnxMeeting) => void,
    onViewNextWeekButtonClick?: () => void
}> = ({
    meetings,
    segment,
    startDate,
    onShowMeeting,
    onBookButtonClick,
    onCancelButtonClick,
    onEnterButtonClick,
    onViewNextWeekButtonClick
}) => {
    const attendeeProfile = useProfile(AffAttendeeProfile)

    const meetingsByDate = meetings.reduce((grouped: Record<string, CnxMeeting[]>, current: CnxMeeting) => {
        const meetingScheduledAtIso = startOfDay(current.scheduledStartAt || new Date(), attendeeProfile?.timezone || undefined)

        if (!grouped[meetingScheduledAtIso.toISOString()]) {
            grouped[meetingScheduledAtIso.toISOString()] = []
        }
        grouped[meetingScheduledAtIso.toISOString()].push(current)
        return grouped
    }, {})

    const groupedMeetings: CnxMeeting[][] = []
    Object.keys(meetingsByDate).sort().forEach((each: string) => {
        groupedMeetings.push(meetingsByDate[each])
    })

    return (
        <div>
            {groupedMeetings.map((groupMeetings: CnxMeeting[]) => {
                const date = groupMeetings[0].scheduledStartAt || new Date()
                const weekday = `${date.toLocaleDateString("en-US", { weekday: "short", timeZone: attendeeProfile?.timezone || undefined })}`.toUpperCase()
                return (
                    <div key={date.toISOString()}>
                        <div id={weekday} className="px-[20px] pt-[32px] pb-[12px] bg-primary-100">
                            <div className="text-title2 text-black-900">{date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: attendeeProfile?.timezone || undefined })}</div>
                            <div className="text-subhead text-black-700">Timezone: {attendeeProfile?.timezone || new Intl.DateTimeFormat("en").resolvedOptions().timeZone}</div>
                        </div>
                        {groupMeetings.map((meeting) => {
                            return (
                                <CnxMeetingCell key={meeting.id}
                                    variant="MEDIUM_LIST"
                                    meeting={meeting}
                                    onClick={(meeting) => onShowMeeting && onShowMeeting(meeting)}
                                    onBookButtonClick={(meeting) => onBookButtonClick && onBookButtonClick(meeting)}
                                    onCancelButtonClick={(meeting, booking) => onCancelButtonClick && onCancelButtonClick(meeting, booking) }
                                    onEnterButtonClick={onEnterButtonClick}
                                    />
                            )
                        })}
                    </div>
                )
            })}

            {meetings.length === 0 &&
                <div className="flex flex-col pt-[72px] items-center bg-primary-100">
                    <Image width="262" height="196" src="/images/ic_graphic_empty_session.svg" alt={`${segment} Classes`} />
                    <p className="mt-[8px] px-[20px] text-body text-center text-black-700">
                        You have not booked any classes this week.<br /><br />
                        Browse and book classes from
                    </p>

                    <Link href={`/classes?from=${startDate.toISOString()}&segment=ALL}`}>
                        <a className="mt-[4px] link-text-accent">All Classes</a>
                    </Link>
                </div>
            }

            <div className="flex bg-primary-100 pt-[72px] pb-[72px]">
                <button type="button"
                    className="mx-auto px-[16px] py-[12px] bg-transparent hover:bg-primary-900 border-[1px] border-primary-900 rounded-[8px] text-callout text-primary-900 hover:text-white-900"
                    onClick={() => onViewNextWeekButtonClick && onViewNextWeekButtonClick()}>
                    {"See Next Week's Schedule"}
                </button>
            </div>
        </div>
    )
}

function CnxMeetingListPage() {
    const router = useRouter()
    const [machine, machineState] = useMachine(CnxMeetingListStateMachine)

    const attendeeProfile = useProfile(AffAttendeeProfile)
    const currentSubscription = useMemo(() => attendeeProfile?.currentSubscription(), [attendeeProfile])

    const [segment, setSegment] = useUpdateableState<"ALL" | "BOOKED">(router.query && router.query.segment && typeof router.query.segment === "string"
        ? (router.query.segment === "BOOKED" ? "BOOKED" : "ALL")
        : "ALL"
    )
    const [fromDate, setFromDate] = useState<Date>(router.query && router.query.from && typeof router.query.from === "string"
        ? new Date(router.query.from)
        : startOfWeek(new Date(), attendeeProfile?.timezone || undefined)
    )
    const [toDate, setToDate] = useState<Date>(endOfWeek(fromDate, attendeeProfile?.timezone || undefined))
    const setDateRange = (from: Date, to: Date) => {
        setFromDate(from)
        setToDate(to)

        router.push(`/classes?from=${from.toISOString()}&segment=${segment}`, undefined, { shallow: true, scroll: true })
    }

    const [unstartedMeeting, setUnstartedMeeting] = useState<CnxMeeting>()

    useRestoredUniverse(useMemo(() => () => {
        if (!attendeeProfile && segment === "BOOKED") {
            localStorage.setItem("afterAuthReturn", `/classes?segment=BOOKED&from=${fromDate.toISOString()}`)
            router.replace(`/auth/login`)
        } else {
            machine.refresh({ segment: segment || "ALL", fromDate, toDate })
        }
    }, [machine, attendeeProfile, router, segment, fromDate, toDate]))

    const bookButtonHandler = useCallback((meeting: CnxMeeting) => {
        if (attendeeProfile) {
            fbTrackEvent("Schedule")
            machine.book(attendeeProfile, meeting)
        } else {
            router.push(`/auth/register?return=${router.asPath}`)
        }
    }, [machine, attendeeProfile, router])

    const cancelBookingButtonHandler = useCallback((meeting: CnxMeeting, booking: CnxBooking) => {
        if (attendeeProfile) {
            machine.cancelBooking(attendeeProfile, meeting, booking)
        }
    }, [machine, attendeeProfile])

    const enterMeetingButtonHandler = useCallback((meeting: CnxMeeting) => {
        if (!meeting.isLiveNow()) {
            setUnstartedMeeting(meeting)
            return
        }
        if (!attendeeProfile) {
            return
        }

        meeting.join(attendeeProfile.id)
            .catch((error) => Sentry.captureException(error))

        const videoRoomUrl = meeting.videoRoom?.actualObject?.roomUrl
        if (!videoRoomUrl) {
            Sentry.captureException(`Video room is ${videoRoomUrl} attendee attempts to join meeting`)
        }

        // window.open() will be blocked by browser popup blocks if it's
        // not called directly from a user called function
        try {
            window.open(videoRoomUrl || "", "_blank")
        } catch (error) {
            Sentry.captureException(error)
        }
    }, [attendeeProfile])

    const changeSegment = useCallback((segment: "ALL" | "BOOKED") => {
        setSegment(segment)
        router.push(`/classes?from=${fromDate.toISOString()}&segment=${segment}`, undefined, { shallow: true, scroll: true })
    }, [fromDate, setSegment, router])

    const navigateNextWeek = useCallback(() => {
        const newFromDate = new Date(fromDate.toISOString())
        newFromDate.setHours(newFromDate.getHours() + (7 * 24))
        const newToDate = endOfWeek(newFromDate)

        setFromDate(newFromDate)
        setToDate(newToDate)

        router.push(`/classes?from=${newFromDate.toISOString()}&segment=${segment}`, undefined, { shallow: true, scroll: true })
    }, [fromDate, segment, router])

    const onDayClick = useCallback((date: Date) => {
        const weekday = date.toLocaleDateString(
            "en-US",
            { weekday: "short", timeZone: attendeeProfile?.timezone || undefined }
        ).toUpperCase()

        const element = document.getElementById(weekday)
        if (!element) {
            return
        }

        const headerOffset = 72
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
        })
    }, [attendeeProfile?.timezone])

    const itemCountOnDay = useCallback((date: Date) => {
        const weekday = date.toLocaleDateString(
            "en-US",
            { weekday: "short", timeZone: attendeeProfile?.timezone || undefined }
        ).toUpperCase()

        switch (machineState.name) {
            case "loading":
            case "presenting":
            case "performingBooking":
            case "performingBookingCancellation":
            case "showingBookingError":
            case "showingInsufficientBalance":
            case "showingMeetingBooked":
                const meetingsByWeekday = machineState.meetings.reduce((grouped: Record<string, CnxMeeting[]>, current: CnxMeeting) => {
                    const meetingScheduledAt = startOfDay(current.scheduledStartAt || new Date(), attendeeProfile?.timezone || undefined)
                    const groupWeekday = `${meetingScheduledAt.toLocaleDateString("en-US", { weekday: "short", timeZone: attendeeProfile?.timezone || undefined })}`.toUpperCase()
                    if (!grouped[groupWeekday]) {
                        grouped[groupWeekday] = []
                    }
                    grouped[groupWeekday].push(current)
                    return grouped
                }, {})
                return meetingsByWeekday[weekday] !== undefined ? meetingsByWeekday[weekday].length : 0
            default:
                return 0
        }
    }, [machineState, attendeeProfile?.timezone])

    return (
        <div>
            <MarketingHeader />

            <div className="pt-[32px] pb-[20px] px-[20px] bg-white-900 shadow">
                <div className="flex">
                    <div className="flex-1 sm:min-w-[300px]">
                        <SegmentedControl value={segment} onChange={(value) => changeSegment(value as any)}>
                            <Segment title="All Classes" value="ALL" key="ALL" />
                            <Segment title="My Classes" value="BOOKED" key="BOOKED" />
                        </SegmentedControl>
                    </div>
                </div>

                <WeekView className="mt-[32px]"
                    from={fromDate}
                    to={toDate}
                    timeZone={attendeeProfile?.timezone || undefined}
                    itemCount={itemCountOnDay}
                    onDayClick={onDayClick}
                    onChange={setDateRange}
                    />
            </div>

            {
                (() => {
                    switch (machineState.name) {
                        case "start":
                            return <LoadingIndicator />
                        case "loading":
                        case "presenting":
                        case "showingError":
                            return (
                                <>
                                    {machineState.name === "showingError" && <ErrorHUD error={machineState.error} onDismiss={() => machine.back()} />}
                                    <MeetingList
                                        meetings={machineState.meetings || []}
                                        segment={segment}
                                        startDate={fromDate}
                                        onShowMeeting={(meeting) => machine.showDetail(meeting)}
                                        onBookButtonClick={bookButtonHandler}
                                        onCancelButtonClick={cancelBookingButtonHandler}
                                        onEnterButtonClick={enterMeetingButtonHandler}
                                        onViewNextWeekButtonClick={navigateNextWeek}
                                        />
                                </>
                            )
                        case "performingBooking":
                        case "performingBookingCancellation":
                        case "showingBookingError":
                            return (
                                <>
                                    {machineState.name === "performingBooking" && <LoadingIndicator message="Processing..." />}
                                    {machineState.name === "performingBookingCancellation" && <LoadingIndicator message="Cancelling..." />}
                                    {machineState.name === "showingBookingError" && <ErrorHUD error={machineState.error} sticky={true} onDismiss={() => machine.back()} />}
                                    <MeetingList
                                        meetings={machineState.meetings || []}
                                        segment={segment}
                                        startDate={fromDate}
                                        onShowMeeting={(meeting) => machine.showDetail(meeting)}
                                        onBookButtonClick={bookButtonHandler}
                                        onCancelButtonClick={cancelBookingButtonHandler}
                                        onEnterButtonClick={enterMeetingButtonHandler}
                                        onViewNextWeekButtonClick={navigateNextWeek}
                                        />
                                </>
                            )
                        case "showingInsufficientBalance":
                            return (
                                <>
                                    <Modal show={true} onDismiss={() => {
                                        machine.back()
                                    }}>
                                        <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle text-center">
                                            <Image width="262" height="192" objectFit="contain" src="/images/ic_graphic_not_enough_balance.svg" alt="Get More Class Passes Now" />
                                            <h4 className="mt-[20px] text-title3 text-black-900">
                                                {!currentSubscription ? "You are on the free plan and can only join free classes" : "You have used all your class passes this month"}
                                            </h4>
                                            <Link href="/profile/membership">
                                                <a className="mt-[32px] button-accent-filled w-full">Get More Class Passes Now</a>
                                            </Link>
                                        </div>
                                    </Modal>
                                    <MeetingList
                                        segment={segment}
                                        startDate={fromDate}
                                        meetings={machineState.meetings || []}
                                        onShowMeeting={(meeting) => machine.showDetail(meeting)}
                                        onBookButtonClick={bookButtonHandler}
                                        onCancelButtonClick={cancelBookingButtonHandler}
                                        onEnterButtonClick={enterMeetingButtonHandler}
                                        onViewNextWeekButtonClick={navigateNextWeek}
                                        />
                                </>
                            )
                        case "showingMeetingBooked":
                            return (
                                <>
                                    <Modal show={true} onDismiss={() => machine.refresh({ segment: segment || "ALL", fromDate, toDate })}>
                                        <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle  text-center">
                                            <Image width="262" height="192" objectFit="contain" src="/images/ic_graphic_class_booked.svg" alt="Session Booked Successfully" />

                                            <h4 className="mt-[20px] text-title3 text-black-900">
                                                Session Booked Successfully
                                            </h4>
                                            <h5 className="mt-[24px] text-subhead text-black-700">{machineState.meeting.title}</h5>
                                            <p className="mt-[4px] text-callout text-tertiary-900">
                                                {machineState.meeting.longScheduledDateDisplay(attendeeProfile?.timezone || undefined)}
                                            </p>

                                            <p className="mt-[24px] text-subhead text-black-900 font-bold">
                                                To join this class, come back to the Live Schedule 10 minutes before the class start
                                                time.
                                            </p>

                                            <a href="https://www.loom.com/share/360f5226e6dd4debaa8e0f48e7c1eba7" target="_blank" rel="noopener noreferrer">
                                                <div className="mt-[20px] text-callout text-center uppercase underline hover:text-accent-900">
                                                    Watch Tutorial
                                                </div>
                                            </a>

                                            <button type="button" className="mt-[32px] button-accent-filled w-full"
                                                onClick={() => machine.refresh({ segment: segment || "ALL", fromDate, toDate })}>
                                                Done
                                            </button>

                                            <p className="mt-[32px] text-black-700">Made a mistake?</p>
                                            <button type="button" className="mt-[8px] link-text-primary"
                                                onClick={() => cancelBookingButtonHandler(machineState.meeting, machineState.booking)}>
                                                Undo Booking
                                            </button>
                                        </div>
                                    </Modal>
                                    <MeetingList
                                        meetings={machineState.meetings || []}
                                        segment={segment}
                                        startDate={fromDate}
                                        onShowMeeting={(meeting) => machine.showDetail(meeting)}
                                        onBookButtonClick={bookButtonHandler}
                                        onCancelButtonClick={cancelBookingButtonHandler}
                                        onEnterButtonClick={enterMeetingButtonHandler}
                                        onViewNextWeekButtonClick={navigateNextWeek}
                                        />
                                </>
                            )
                        case "showingDetail":
                            router.push(`/classes/${machineState.meeting.id}?return=${encodeURIComponent(`from=${fromDate.toISOString()}`)}`)
                            return <MeetingList
                                meetings={machineState.meetings || []}
                                segment={segment}
                                startDate={fromDate}
                                onShowMeeting={(meeting) => machine.showDetail(meeting)}
                                onBookButtonClick={bookButtonHandler}
                                onCancelButtonClick={cancelBookingButtonHandler}
                                onEnterButtonClick={enterMeetingButtonHandler}
                                onViewNextWeekButtonClick={navigateNextWeek}
                                />
                    }
                })()
            }

            <Modal show={unstartedMeeting !== undefined} onDismiss={() => setUnstartedMeeting(undefined)}>
                <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle  text-center">
                    <Image width="262" height="192" objectFit="contain" src="/images/ic_graphic_alert.svg" alt="Class Haven't Start Yet" />

                    <h4 className="mt-[20px] text-title3 text-black-900">
                        {"This class hasn't started yet."}
                    </h4>

                    <p className="mt-[24px] text-subhead text-black-700">
                        Please come back and press <b>JOIN CLASS</b> on
                    </p>
                    <p className="mt-[4px] text-callout text-tertiary-900 text-center">
                        {unstartedMeeting?.joinAtTimeDisplay(attendeeProfile?.timezone || undefined)}
                    </p>

                    <button type="button" className="mt-[32px] button-accent-filled w-full"
                        onClick={() => setUnstartedMeeting(undefined)}>
                        Done
                    </button>
                </div>
            </Modal>
        </div>
    )
}

CnxMeetingListPage.Layout = AFFSidebarLayout

export default CnxMeetingListPage
